/*
 * The HeaderMenu only displays if we have an authenticated user.
 * It is responsible for displaying the welcome message and the dropdown menu for logged in users
 * spinner is from : http://tobiasahlin.com/spinkit/
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as localForage from 'localforage';
import * as React from 'react';
import { ButtonToolbar, Dropdown, MenuItem } from 'react-bootstrap';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { toggleUserAlertModal } from '../../actions/manageUserAlertActions';
import {
    toggleContactUsModal,
    toggleEditProfileModal,
    userLogout
} from '../../actions/userActions';
import { Iuser } from '../../models';
import { IMenuSection, IMenuItem } from '../../models/headerMenu';

import { isEmpty } from 'lodash';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toggleTutorial } from '../../actions/tutorialActions';
import { constants } from '../../constants/constants';
import {
    MENU_EVENT_KEYS,
    INTERNAL_EMAIL_DOMAINS
} from '../../constants/headerMenuConfig';
import { svgIcons } from '../../constants/svgIcons';
import { isAdmin } from '../../helpers/securityUtils';
import { IinitialState } from '../../reducers';
import { selectIsLoading } from '../../reducers/commonReducers';
import { getActiveLocalCountry } from '../../reducers/manageUserReducer';
import EditProfileModal from './EditProfileModal';
import msalInstance from '../auth/MsalInstance';
import alertBellIcon from '../../images/icons/alert_bell.svg';
import helpManualIcon from '../../images/icons/BM_HelpManual.svg';
import { tiles } from '../../constants/tiles';
import { menuParentKeysEnum } from '../../models-enums';
import { Itile } from '../../models';
import { getServiceUrlByCountry } from '../../helpers/urlHelpers';

interface Iprops extends RouteComponentProps {
    user: Iuser;
    alertsCount: number;
    userLogout: any;
    loading: boolean;
    toggleTutorial: typeof toggleTutorial;
    activeLocalCountry: string;
    toggleEditProfileModal: any;
    toggleContactUsModal: any;
    toggleUserAlertModal: typeof toggleUserAlertModal;
}

interface Istate {
    menuOpen: boolean;
    viewMode: 'admin' | 'facility';
}

// LocalForage key for persisting view mode
const VIEW_MODE_STORAGE_KEY = 'headerMenu.viewMode';

class Header extends React.Component<Iprops & WithTranslation, Istate> {
    constructor(props: Iprops & WithTranslation) {
        super(props);
        this.state = {
            menuOpen: false,
            viewMode: 'facility'
        };
    }

    async componentDidMount() {
        // Load saved view mode from localforage
        try {
            const savedViewMode = await localForage.getItem<
                'admin' | 'facility'
            >(VIEW_MODE_STORAGE_KEY);
            if (
                savedViewMode &&
                (savedViewMode === 'admin' || savedViewMode === 'facility')
            ) {
                this.setState({ viewMode: savedViewMode });
            }
        } catch (error) {
            console.warn('Failed to load saved view mode:', error);
        }
    }

    // Save view mode to localforage
    private async saveViewMode(viewMode: 'admin' | 'facility') {
        try {
            await localForage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
        } catch (error) {
            console.warn('Failed to save view mode:', error);
        }
    }

    // Get active country - prioritize local selection over global user country
    private getActiveCountry = (): string => {
        const { user, activeLocalCountry } = this.props;
        if (activeLocalCountry !== '') {
            return activeLocalCountry;
        } else {
            return user.countryID;
        }
    };

    // Generate menu sections dynamically from tiles
    private generateMenuSectionsFromTiles = (): IMenuSection[] => {
        const { user } = this.props;

        if (!user?.securityFunctions) {
            return [];
        }

        // Filter tiles that should appear in menu and user has access to
        const menuTiles = tiles.filter(tile => {
            // Skip tiles that are explicitly hidden from menu
            if (tile.shouldHideOnMenu === true) {
                return false;
            }

            // Skip tiles with disabled security functions
            if (tile.securityFunctions.includes('temporarily disabled')) {
                return false;
            }

            // Check if user has required security functions
            return this.hasTileAccess(tile, user);
        });

        // Group tiles by parent key
        const tilesByParent: { [key: number]: Itile[] } = {};
        menuTiles.forEach(tile => {
            if (!tilesByParent[tile.parentKey]) {
                tilesByParent[tile.parentKey] = [];
            }
            tilesByParent[tile.parentKey].push(tile);
        });

        // Generate menu sections in specific order
        const menuSections: IMenuSection[] = [];

        // ADMIN section (excluding job, report, sales, and facility tiles)
        if (tilesByParent[menuParentKeysEnum.admin]) {
            const adminTiles = tilesByParent[menuParentKeysEnum.admin]
                .filter(
                    tile =>
                        ![
                            'manageJobs',
                            'workorders',
                            'jobComments',
                            'manageReports',
                            'reportBuilder',
                            'suggestedParts',
                            'productqueue',
                            'products',
                            'facilityqueue',
                            'alerts'
                        ].includes(tile.title)
                )
                .sort((a, b) => a.order - b.order);

            const adminItems: IMenuItem[] = adminTiles.map(tile => ({
                eventKey: this.generateEventKeyFromTile(tile),
                label: this.getLabelFromTile(tile),
                description: this.getDescriptionFromTile(tile),
                url: tile.url,
                securityFunctions: tile.securityFunctions as string[]
            }));

            if (adminItems.length > 0) {
                menuSections.push({
                    title: 'ADMIN',
                    items: adminItems
                });
            }
        }

        // FACILITY section (including facility queue and alerts from admin)
        const facilityTilesFromFacility =
            tilesByParent[menuParentKeysEnum.facility] || [];
        const facilityTilesFromAdmin =
            tilesByParent[menuParentKeysEnum.admin]?.filter(tile =>
                ['facilityqueue', 'alerts'].includes(tile.title)
            ) || [];

        // Add inventory and assetPhotos from default tiles for admin access
        const facilityTilesFromDefault =
            tilesByParent[menuParentKeysEnum.default]?.filter(tile =>
                ['inventory', 'assetPhotos'].includes(tile.title)
            ) || [];

        const allFacilityTiles = [
            ...facilityTilesFromFacility,
            ...facilityTilesFromAdmin,
            ...facilityTilesFromDefault
        ];

        // Sort by order
        allFacilityTiles.sort((a, b) => a.order - b.order);

        if (allFacilityTiles.length > 0) {
            const facilityItems: IMenuItem[] = allFacilityTiles.map(tile => {
                // Override labels for better clarity in admin view
                let label = this.getLabelFromTile(tile);
                let description = this.getDescriptionFromTile(tile);

                if (tile.title === 'inventory') {
                    label = 'Asset Details';
                    description = 'View and manage all assets';
                } else if (tile.title === 'assetPhotos') {
                    label = 'Asset Photos';
                    description = 'Photos from WO and inspections';
                }

                return {
                    eventKey: this.generateEventKeyFromTile(tile),
                    label,
                    description,
                    url: tile.url,
                    securityFunctions: tile.securityFunctions as string[]
                };
            });

            menuSections.push({
                title: 'FACILITY',
                items: facilityItems
            });
        }

        // JOBS section (from admin and default tiles)
        const jobTilesFromAdmin =
            tilesByParent[menuParentKeysEnum.admin]?.filter(tile =>
                ['manageJobs', 'jobComments'].includes(tile.title)
            ) || [];
        const jobTilesFromDefault =
            tilesByParent[menuParentKeysEnum.default]?.filter(tile =>
                ['workorders'].includes(tile.title)
            ) || [];

        const jobTiles = [...jobTilesFromAdmin, ...jobTilesFromDefault].sort(
            (a, b) => a.order - b.order
        );

        if (jobTiles.length > 0) {
            const jobItems: IMenuItem[] = jobTiles.map(tile => ({
                eventKey: this.generateEventKeyFromTile(tile),
                label: this.getLabelFromTile(tile),
                description: this.getDescriptionFromTile(tile),
                url: tile.url,
                securityFunctions: tile.securityFunctions as string[]
            }));

            menuSections.push({
                title: 'JOBS',
                items: jobItems
            });
        }

        // SALES section (including suggested parts, product queue, and products from admin)
        const salesTilesFromSales =
            tilesByParent[menuParentKeysEnum.sales]?.filter(
                tile => !['quotes_sales'].includes(tile.title) // Hide sales quotes for now
            ) || [];
        const salesTilesFromAdmin =
            tilesByParent[menuParentKeysEnum.admin]?.filter(tile =>
                ['suggestedParts', 'productqueue', 'products'].includes(
                    tile.title
                )
            ) || [];

        const allSalesTiles = [
            ...salesTilesFromSales,
            ...salesTilesFromAdmin
        ].sort((a, b) => a.order - b.order);

        if (allSalesTiles.length > 0) {
            const salesItems: IMenuItem[] = allSalesTiles.map(tile => ({
                eventKey: this.generateEventKeyFromTile(tile),
                label: this.getLabelFromTile(tile),
                description: this.getDescriptionFromTile(tile),
                url: tile.url,
                securityFunctions: tile.securityFunctions as string[]
            }));

            menuSections.push({
                title: 'SALES',
                items: salesItems
            });
        }

        // REPORTS section
        const reportTiles = [
            ...(tilesByParent[menuParentKeysEnum.admin]?.filter(tile =>
                ['manageReports', 'reportBuilder'].includes(tile.title)
            ) || []),
            ...(tilesByParent[menuParentKeysEnum.dashboard]?.filter(
                tile =>
                    tile.title.includes('report') ||
                    tile.title.includes('Report')
            ) || [])
        ].sort((a, b) => a.order - b.order);

        if (reportTiles.length > 0) {
            const reportItems: IMenuItem[] = reportTiles.map(tile => ({
                eventKey: this.generateEventKeyFromTile(tile),
                label: this.getLabelFromTile(tile),
                description: this.getDescriptionFromTile(tile),
                url: tile.url,
                securityFunctions: tile.securityFunctions as string[]
            }));

            menuSections.push({
                title: 'REPORTS',
                items: reportItems
            });
        }

        // Add settings section (always present)
        menuSections.push({
            title: 'SETTINGS',
            items: [
                // Only show "View as Facility" if user has admin privileges
                ...(isAdmin(user)
                    ? [
                          {
                              eventKey: MENU_EVENT_KEYS.VIEW_AS_FACILITY,
                              label: 'View as Facility',
                              description:
                                  'Show all menu items for a facility role',
                              icon: 'building'
                          }
                      ]
                    : []),
                {
                    eventKey: MENU_EVENT_KEYS.PROFILE,
                    label: 'Profile',
                    description:
                        'Set preferences standards, filter, language, and updates',
                    icon: ['far', 'user']
                },
                {
                    eventKey: MENU_EVENT_KEYS.CONTACT_US,
                    label: 'Contact Us',
                    description: 'Find support and report issues',
                    icon: 'mail'
                },
                {
                    eventKey: MENU_EVENT_KEYS.LOGOUT,
                    label: 'Logout',
                    description: 'Logout from the system',
                    icon: ['far', 'sign-out']
                }
            ]
        });

        return menuSections;
    };

    // Check if user has access to a specific tile
    private hasTileAccess = (tile: Itile, user: Iuser): boolean => {
        if (!tile.securityFunctions || tile.securityFunctions.length === 0) {
            return true;
        }

        return constants.hasSecurityFunction(
            user,
            tile.securityFunctions as string[]
        );
    };

    // Generate event key from tile
    private generateEventKeyFromTile = (tile: Itile): string => {
        return tile.url.replace('/', '').replace('_', '-');
    };

    // Get display label from tile
    private getLabelFromTile = (tile: Itile): string => {
        // Map tile titles to more user-friendly labels
        const titleMap: { [key: string]: string } = {
            quotes: 'Quotes',
            quotes_sales: 'Sales Quotes',
            buyTraining: 'Buy Training',
            leads: 'Leads',
            inventory: 'Inventory',
            manageUser: 'Users',
            manageUserQueue: 'New User Queue',
            Documents: 'Facility Documents',
            manageReports: 'Report Printing',
            reportBuilder: 'Report Builder',
            facilities: 'Facility Information',
            locations: 'Facility Locations',
            manageJobs: 'Jobs',
            alerts: 'Alerts',
            standardTextLibrary: 'Standard Text Library',
            workorders: 'Work Orders',
            facilityqueue: 'Facility Queue',
            productqueue: 'New Products Queue',
            products: 'Products',
            manageMeasurementPointList: 'Measurements',
            brands: 'Brands',
            myTrainingPortal: 'Training Portal',
            manageTeam: 'Team Management',
            jobComments: 'Job Comments',
            pmp: 'PMP Dashboard',
            inspection: 'Asset Status',
            home: 'Home',
            assetPhotos: 'Asset Photos',
            suggestedParts: 'Suggested Parts'
        };

        return titleMap[tile.title] || tile.title;
    };

    // Get description from tile
    private getDescriptionFromTile = (tile: Itile): string => {
        // Map tile titles to descriptions matching the screenshot
        const descriptionMap: { [key: string]: string } = {
            manageUser: 'Manage user accounts and permissions',
            manageUserQueue: 'Review and approve pending user registrations',
            brands: 'Manage equipment brands and manufacturers',
            alerts: 'Monitor equipment alerts',
            locations: 'Manage facility rooms and locations',
            facilities: 'View and manage facility details',
            facilityqueue: 'Review pending facility updates',
            manageJobs: 'Manage service jobs and assignments',
            jobComments: 'View and manage job-related comments',
            manageMeasurementPointList: 'Configure measurement point lists',
            workorders: 'Manage customer work orders',
            leads: 'Track and manage sales opportunities',
            products: 'Manage product catalog',
            productqueue: 'Review pending product submissions',
            suggestedParts: 'Manage recommended replacement parts',
            manageReports: 'Generate and print reports',
            reportBuilder: 'Create custom reports',
            standardTextLibrary: 'Manage reusable report text templates'
        };

        return (
            descriptionMap[tile.title] ||
            tile.description ||
            `Navigate to ${tile.title}`
        );
    };

    // Menu sections filtered by user's security functions
    private get menuSections(): IMenuSection[] {
        return this.getFilteredMenuSections();
    }

    private getFilteredMenuSections = (): IMenuSection[] => {
        const { user } = this.props;

        if (!user?.securityFunctions) {
            return [];
        }

        // If in facility view mode, return simplified menu
        if (this.state.viewMode === 'facility') {
            return this.getFacilityMenuSections();
        }

        // Use tiles-based menu generation
        return this.generateMenuSectionsFromTiles();
    };

    private getFacilityMenuSections = (): IMenuSection[] => {
        const { user } = this.props;
        const activeCountryID = this.getActiveCountry();

        // Get facility-relevant tiles with user access checking
        const facilityTiles = tiles.filter(tile => {
            // Only include certain tiles for facility view
            const facilityRelevantTiles = [
                'inventory',
                'assetPhotos',
                'Documents',
                'buyTraining',
                'myTrainingPortal'
            ];

            return (
                facilityRelevantTiles.includes(tile.title) &&
                this.hasTileAccess(tile, user) &&
                tile.securityFunctions &&
                !tile.securityFunctions.includes('temporarily disabled')
            );
        });

        // Create facility menu sections based on filtered tiles
        const sections: IMenuSection[] = [];

        // MY EQUIPMENT section
        const inventoryTile = facilityTiles.find(t => t.title === 'inventory');
        const assetPhotosTile = facilityTiles.find(
            t => t.title === 'assetPhotos'
        );
        if (inventoryTile) {
            const equipmentItems = [
                {
                    eventKey: 'my-equipment',
                    label: 'Equipment Overview',
                    description: 'Overview and operational status',
                    url: '/dashboard/equipment',
                    securityFunctions: inventoryTile.securityFunctions as string[]
                },
                {
                    eventKey: 'view-assets',
                    label: 'Asset Details',
                    description: 'View and manage all assets',
                    url: '/inventory',
                    securityFunctions: inventoryTile.securityFunctions as string[]
                }
            ];

            if (assetPhotosTile) {
                equipmentItems.push({
                    eventKey: 'asset-photos',
                    label: 'Asset Photos',
                    description: 'Photos from WO and inspections',
                    url: assetPhotosTile.url,
                    securityFunctions: assetPhotosTile.securityFunctions as string[]
                });
            }

            sections.push({
                title: 'MY EQUIPMENT',
                items: equipmentItems
            });
        }

        // MY SERVICES & COMPLIANCE section
        const complianceItems = [];

        // Add Service & Operations as first item
        complianceItems.push({
            eventKey: 'service-operations',
            label: 'Service & Operations',
            description: 'Inspections, Maintenance, Work orders',
            url: '/dashboard/service-grid',
            securityFunctions: []
        });

        const documentsTile = facilityTiles.find(t => t.title === 'Documents');
        if (documentsTile) {
            complianceItems.push({
                eventKey: 'document-library',
                label: 'Document Library',
                description:
                    'Organized and searchable documentation for all equipment',
                url: documentsTile.url,
                securityFunctions: documentsTile.securityFunctions as string[]
            });
        }

        if (complianceItems.length > 0) {
            sections.push({
                title: 'MY SERVICES & OPERATIONS',
                items: complianceItems
            });
        }

        // MY TRAINING section
        const trainingItems = [];
        const buyTrainingTile = facilityTiles.find(
            t => t.title === 'buyTraining'
        );
        if (buyTrainingTile) {
            trainingItems.push({
                eventKey: 'buy-training-classes',
                label: 'Buy Training Classes',
                description: 'Purchase training courses for staff',
                url: buyTrainingTile.url,
                securityFunctions: buyTrainingTile.securityFunctions as string[]
            });
        }

        const trainingPortalTile = facilityTiles.find(
            t => t.title === 'myTrainingPortal'
        );
        if (trainingPortalTile) {
            trainingItems.push({
                eventKey: 'my-training-portal',
                label: 'My Training Portal',
                description: 'Access assigned training materials',
                url: trainingPortalTile.url,
                securityFunctions: trainingPortalTile.securityFunctions as string[]
            });
        }

        if (trainingItems.length > 0) {
            sections.push({
                title: 'MY TRAINING',
                items: trainingItems
            });
        }

        // MY RESOURCE LIBRARY section (duplicate of documents)
        if (documentsTile) {
            sections.push({
                title: 'OTHER SERVICES',
                items: [
                    {
                        eventKey: 'services-offered',
                        label: 'Services Offered',
                        description: 'Other services we offer',
                        url: getServiceUrlByCountry(activeCountryID),
                        securityFunctions: documentsTile.securityFunctions as string[]
                    }
                ]
            });
        }

        // SETTINGS section (always present)
        sections.push({
            title: 'SETTINGS',
            items: [
                // Only show "View as Admin" if user has admin privileges
                ...(isAdmin(user)
                    ? [
                          {
                              eventKey: MENU_EVENT_KEYS.VIEW_AS_ADMIN,
                              label: 'View as Admin',
                              description:
                                  'Show all menu items for my Administrative role',
                              icon: 'userShield'
                          }
                      ]
                    : []),
                {
                    eventKey: MENU_EVENT_KEYS.PROFILE,
                    label: 'Profile',
                    description:
                        'Set preferences standards, filter, language, and updates',
                    icon: ['far', 'user']
                },
                {
                    eventKey: MENU_EVENT_KEYS.CONTACT_US,
                    label: 'Contact Us',
                    description: 'Find support and report issues',
                    icon: 'mail'
                },
                {
                    eventKey: MENU_EVENT_KEYS.LOGOUT,
                    label: 'Logout',
                    description: 'Logout from the system',
                    icon: ['far', 'sign-out']
                }
            ]
        });

        return sections;
    };

    private hasMenuItemAccess = (item: IMenuItem, user: Iuser): boolean => {
        // Settings items (Profile, Logout, etc.) have no security requirements
        if (!item.securityFunctions || item.securityFunctions.length === 0) {
            return true;
        }

        // Use the existing security function checker from constants
        return constants.hasSecurityFunction(user, item.securityFunctions);
    };

    handleMenuSelect = (eventKey: any) => {
        const { history } = this.props;
        const eventKeyStr = String(eventKey);

        // Handle special action items
        switch (eventKeyStr) {
            case MENU_EVENT_KEYS.PROFILE: {
                this.props.toggleEditProfileModal();
                break;
            }
            case MENU_EVENT_KEYS.CONTACT_US: {
                this.props.toggleContactUsModal();
                break;
            }
            case MENU_EVENT_KEYS.LOGOUT: {
                this.props.userLogout(this.props.t);
                break;
            }
            case MENU_EVENT_KEYS.VIEW_AS_FACILITY: {
                this.setState({ viewMode: 'facility' });
                this.saveViewMode('facility');
                // Don't close menu for view toggles - return early
                return;
            }
            case MENU_EVENT_KEYS.VIEW_AS_ADMIN: {
                this.setState({ viewMode: 'admin' });
                this.saveViewMode('admin');
                // Don't close menu for view toggles - return early
                return;
            }
            default: {
                // Handle navigation items by finding the URL in menu configuration
                const menuItem = this.findMenuItemByEventKey(eventKeyStr);
                if (menuItem?.url) {
                    if (this.isExternalUrl(menuItem.url)) {
                        window.open(
                            menuItem.url,
                            '_blank',
                            'noopener,noreferrer'
                        );
                    } else {
                        history.push(menuItem.url);
                    }
                }
                break;
            }
        }

        this.setState({ menuOpen: false });
    };

    private findMenuItemByEventKey = (
        eventKey: string
    ): IMenuItem | undefined => {
        for (const section of this.menuSections) {
            const foundItem = section.items.find(
                menuItem => menuItem.eventKey === eventKey
            );
            if (foundItem) {
                return foundItem;
            }
        }
        return undefined;
    };

    private isExternalUrl = (url: string): boolean => {
        // Check if URL starts with http:// or https:// (external)
        // or if it's a different domain than the current host
        return url.startsWith('http://') || url.startsWith('https://');
    };

    showTutorial() {
        // return false; // jfbloom22 temporarily disabling tutorials for prod deploy
        const { tutorial } = constants;
        const { pathname } = this.props.location;
        const modifiedPathname = pathname.slice(1); // Removes slash

        if (tutorial.hasOwnProperty(modifiedPathname)) {
            return true;
        } else return false;
    }

    isEmailDomainInternal(email: string): boolean {
        return INTERNAL_EMAIL_DOMAINS.some(domain => email.includes(domain));
    }

    private renderMenuItem = (item: any) => {
        const isExternal = item.url && this.isExternalUrl(item.url);

        return (
            <React.Fragment key={item.eventKey}>
                <MenuItem
                    eventKey={item.eventKey}
                    className="menu-item-button"
                    onClick={e => {
                        // Prevent default navigation for external URLs
                        if (isExternal) {
                            e.preventDefault();
                            e.stopPropagation();
                            window.open(
                                item.url,
                                '_blank',
                                'noopener,noreferrer'
                            );
                            this.setState({ menuOpen: false });
                        } else {
                            this.handleMenuSelect(item.eventKey);
                        }
                    }}
                    href={isExternal ? item.url : undefined}
                    target={isExternal ? '_blank' : undefined}
                    rel={isExternal ? 'noopener noreferrer' : undefined}
                >
                    <span className="menu-item-label">
                        {item.icon &&
                        svgIcons[item.icon as keyof typeof svgIcons] ? (
                            svgIcons[item.icon as keyof typeof svgIcons]
                        ) : item.icon ? (
                            <FontAwesomeIcon icon={item.icon} fixedWidth />
                        ) : null}

                        {item.label}
                    </span>
                    <div className="menu-item-description">
                        {item.description}
                    </div>
                </MenuItem>
            </React.Fragment>
        );
    };

    private renderMenuSection = (section: any) => {
        const isSettingsSection = section.title === 'SETTINGS';
        return (
            <div
                className={`menu-section ${
                    isSettingsSection ? 'settings-section' : ''
                }`}
                key={section.title}
            >
                <div className="menu-section-header">{section.title}</div>
                {section.items.map(this.renderMenuItem)}
            </div>
        );
    };

    render() {
        const { t, user } = this.props;
        const isUserFullyLoggedIn =
            user?.id.length &&
            msalInstance.msalApp?.getAllAccounts().length > 0;

        if (!isUserFullyLoggedIn && !this.props.loading) {
            return null;
        }
        const menuClass = this.state.menuOpen
            ? `menu-open ${
                  this.state.viewMode === 'facility' ? 'facility-view' : ''
              }`
            : '';
        return (
            <>
                <div className="profile">
                    <div className="profile-text">
                        <div className="help-manual-icon-wrapper">
                            <a
                                href="https://doc.clickup.com/9012013137/d/h/8cjgu2h-3292/26881703f6fb6c9"
                                target="_blank"
                                rel="noopener noreferrer"
                                title="How To Manual"
                            >
                                <img
                                    src={helpManualIcon}
                                    alt="How To Manual"
                                    className="help-manual-icon"
                                />
                            </a>
                        </div>
                        <div className="alert-notification-badge">
                            {this.props.alertsCount > 0 ? (
                                <img
                                    src={alertBellIcon}
                                    alt="icon"
                                    className="notification-icon"
                                    onClick={this.props.toggleUserAlertModal}
                                    height="30"
                                />
                            ) : (
                                <img
                                    src={alertBellIcon}
                                    alt="icon"
                                    className="notification-icon"
                                    onClick={this.props.toggleUserAlertModal}
                                />
                            )}
                            {this.props.alertsCount > 0 ? (
                                <span className="badge">
                                    {this.props.alertsCount}
                                </span>
                            ) : (
                                ''
                            )}
                        </div>
                        <span className="welcome-text">{t('welcome')}</span>
                        <span className="name">{this.props.user.first}</span>
                        <span className="vertical" />
                    </div>
                    <ButtonToolbar className="header-menu">
                        <Dropdown
                            open={this.state.menuOpen}
                            onToggle={isOpen => {
                                this.setState({ menuOpen: isOpen });
                            }}
                            id="header-dropdown"
                            onSelect={this.handleMenuSelect}
                        >
                            <Dropdown.Toggle
                                bsStyle="primary"
                                className="header-menu-button"
                            >
                                <span>Menu</span>
                                <FontAwesomeIcon
                                    fixedWidth
                                    icon={
                                        this.state.menuOpen
                                            ? ['far', 'times']
                                            : ['far', 'bars']
                                    }
                                />
                            </Dropdown.Toggle>

                            <Dropdown.Menu className={menuClass}>
                                {this.menuSections.map(this.renderMenuSection)}

                                <small className="menu-version">
                                    v: {process.env.REACT_APP_VERSION}
                                </small>
                            </Dropdown.Menu>
                        </Dropdown>
                    </ButtonToolbar>
                </div>
                <EditProfileModal colorButton="warning" />
            </>
        );
    }
}

const mapStateToProps = (
    state: IinitialState,
    ownProps: Iprops & RouteComponentProps<{}>
) => {
    let alertsCount = 0;

    if (isEmpty(state.alertsByID) === false) {
        for (const key of Object.keys(state.alertsByID)) {
            if (state.alertsByID[key].isActive === true) {
                alertsCount++;
            }
        }
    }

    return {
        user: state.user,
        loading: selectIsLoading(state),
        alertsCount,
        activeLocalCountry: getActiveLocalCountry(state)
    };
};

export default withRouter(
    withTranslation('common')(
        connect(mapStateToProps, {
            userLogout,
            toggleEditProfileModal,
            toggleContactUsModal,
            toggleUserAlertModal,
            toggleTutorial
        })(Header)
    )
);
